/**
 * VoiceNoteButton — Spanish-first dictation for field crew.
 *
 * How it works:
 *  1. Field worker taps "🎤 Dictate in Spanish" — browser's built-in
 *     webkitSpeechRecognition kicks in with lang="es-ES" (free, works on
 *     Chrome/Safari mobile without any API key).
 *  2. On stop we get an ES transcript. We POST it to /api/translate which
 *     returns an EN translation via Claude.
 *  3. Parent gets both strings via onNote({ es, en }). Parent decides
 *     whether to store one, the other, or both — but the "office record"
 *     use-case wants the English translation saved to `description`.
 *
 * If the browser doesn't support Web Speech API we render a disabled state
 * with a hint to switch browser.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Microphone, StopCircle } from "@phosphor-icons/react";
import { useT } from "@/i18n";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token")}` } });

// eslint-disable-next-line no-undef
const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function VoiceNoteButton({ onNote }) {
  const { t } = useT();
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [esText, setEsText] = useState("");
  const [enText, setEnText] = useState("");
  const recRef = useRef(null);

  if (!SR) {
    return (
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic p-2 border border-dashed border-slate-300" data-testid="voice-note-unsupported">
        {t("vn.unsupported")}
      </div>
    );
  }

  const start = () => {
    setEsText(""); setEnText("");
    const rec = new SR();
    rec.lang = "es-ES";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join(" ").trim();
      setEsText(transcript);
    };
    rec.onerror = (e) => {
      toast.error(e.error === "no-speech" ? t("vn.no_transcript") : `Voice: ${e.error}`);
      setListening(false);
    };
    rec.onend = async () => {
      setListening(false);
      // Grab the last-known transcript from state via a functional read
      setEsText(prev => {
        if (prev) translate(prev);
        return prev;
      });
    };
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const stop = () => { try { recRef.current?.stop(); } catch {} };

  const translate = async (text) => {
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/translate`,
        { text, source_lang: "es", target_lang: "en" }, auth());
      setEnText(data.translated_text || "");
    } catch {
      toast.error(t("vn.translate_fail"));
    } finally { setBusy(false); }
  };

  const useEn = () => {
    if (!enText) return;
    onNote?.({ es: esText, en: enText });
    setEsText(""); setEnText("");
  };

  return (
    <div className="space-y-1" data-testid="voice-note">
      <div className="flex items-center gap-2">
        {!listening ? (
          <button type="button" onClick={start} disabled={busy}
            data-testid="voice-note-start"
            className="h-9 px-3 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
            <Microphone weight="fill" size={12}/> {t("vn.dictate_es")}
          </button>
        ) : (
          <button type="button" onClick={stop}
            data-testid="voice-note-stop"
            className="h-9 px-3 bg-black text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 animate-pulse">
            <StopCircle weight="fill" size={12}/> {t("vn.listening")}
          </button>
        )}
        <span className="text-[10px] italic text-slate-500">{t("vn.mic_hint")}</span>
      </div>
      {esText && (
        <div className="p-2 bg-amber-50 border-l-4 border-amber-500" data-testid="voice-note-es">
          <div className="text-[9px] font-black uppercase tracking-widest text-amber-800 mb-0.5">{t("vn.original_es")}</div>
          <div className="text-xs text-slate-800">{esText}</div>
        </div>
      )}
      {busy && (
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 italic">{t("vn.translating")}</div>
      )}
      {enText && (
        <div className="p-2 bg-emerald-50 border-l-4 border-emerald-500" data-testid="voice-note-en">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="text-[9px] font-black uppercase tracking-widest text-emerald-800">{t("vn.translation_en")}</div>
            <button type="button" onClick={useEn}
              data-testid="voice-note-use-en"
              className="h-6 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[9px] tracking-widest">
              {t("vn.use_translation")}
            </button>
          </div>
          <div className="text-xs text-slate-800">{enText}</div>
        </div>
      )}
    </div>
  );
}
